import { useState } from "react";
import { useNavigate } from "react-router";
import { captureEvent } from "@/src/lib/posthog";
import {
  bulkDeleteMonitoringEntries,
  exportReport,
  type MonitoringEntry,
  type MonitoringKpi,
} from "@/src/services/monitoring_service";
import { EntriesTable, EntriesTableHeader } from "./entries-table";
import {
  ENTRIES_COLUMNS_META,
  sortEntries,
  type ColumnId,
  type EntriesSort,
  type EntriesSortKey,
} from "./entries-table-utils";
import { MonitoringHeader, Toolbar, type ProcessingBadgeStatus } from "./monitoring-header";
import { type EntryFilter } from "./monitoring-filters";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import { PaginationFooter } from "@/components/pagination-footer";
import { TableColumnsMenu } from "@/components/table-columns-menu";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { useToast } from "@/components/action-feedback";

type MonitoringResultsProps = {
  entries: MonitoringEntry[];
  count: number;
  kpi: MonitoringKpi;
  kpiLoading?: boolean;
  processingStatus?: ProcessingBadgeStatus;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  order: readonly ColumnId[];
  hidden: ReadonlySet<ColumnId>;
  widths: Partial<Record<ColumnId, number>>;
  onReorder: (next: ColumnId[]) => void;
  onToggleHidden: (id: ColumnId) => void;
  onResizeColumn: (id: ColumnId, px: number) => void;
  onResetColumns: () => void;
  rowsPerPage: number;
  onRowsPerPageChange: (n: number) => void;
  onNewMonitoring?: () => void;
  onUploadBatch?: () => void;
  canManageEntries?: boolean;
  onReload?: () => void;
  query?: string;
  onQueryChange?: (q: string) => void;
  filter?: EntryFilter;
  onFilterChange?: (f: EntryFilter) => void;
  searching?: boolean;
};

export function MonitoringResults({
  entries,
  count,
  kpi,
  kpiLoading = false,
  processingStatus,
  page,
  pageCount,
  onPageChange,
  order,
  hidden,
  widths,
  onReorder,
  onToggleHidden,
  onResizeColumn,
  onResetColumns,
  rowsPerPage,
  onRowsPerPageChange,
  onNewMonitoring,
  onUploadBatch,
  canManageEntries = true,
  onReload,
  query = "",
  onQueryChange,
  filter = "All Entries",
  onFilterChange,
  searching = false,
}: MonitoringResultsProps) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<EntriesSort>(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();
  const showToast = useToast();

  const pageKey = `${page}|${rowsPerPage}`;
  const [selectionPageKey, setSelectionPageKey] = useState(pageKey);
  if (selectionPageKey !== pageKey) {
    setSelectionPageKey(pageKey);
    if (selectedIds.size > 0) setSelectedIds(new Set());
  }

  const sorted = sortEntries(entries, sort);
  const selectableSorted = sorted.filter((e) => e.status !== "PROCESSING");

  async function handleDownloadReport() {
    const res = await exportReport(filter, query.trim() || undefined);
    if (res.ok) {
      showToast({ tone: "success", text: res.value.message });
    } else {
      showToast({ tone: "error", text: "Couldn't request the report. Please try again." });
    }
  }

  const allSelected =
    selectableSorted.length > 0 && selectableSorted.every((e) => selectedIds.has(e.id));
  const someSelected = selectableSorted.some((e) => selectedIds.has(e.id));
  const visibleSelectedIds = selectableSorted.filter((e) => selectedIds.has(e.id)).map((e) => e.id);

  function toggleSelect(id: string) {
    if (!selectableSorted.some((e) => e.id === id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const everySelected = selectableSorted.every((e) => prev.has(e.id));
      selectableSorted.forEach((e) => (everySelected ? next.delete(e.id) : next.add(e.id)));
      return next;
    });
  }

  function resetFilters() {
    onQueryChange?.("");
    onFilterChange?.("All Entries");
  }

  function handleSort(key: EntriesSortKey) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  }

  async function confirmDelete() {
    const ids = visibleSelectedIds;
    if (ids.length === 0) return;
    setDeleting(true);
    setDeleteError(undefined);
    const res = await bulkDeleteMonitoringEntries(ids);
    setDeleting(false);
    if (!res.ok) {
      setDeleteError(
        `Couldn't delete the selected ${ids.length === 1 ? "name" : "names"}. Try again.`,
      );
      return;
    }
    captureEvent("Delete monitoring", { count: ids.length });
    setConfirmDeleteOpen(false);
    setSelectedIds(new Set());
    setSelectMode(false);
    onReload?.();
    showToast({
      tone: "success",
      text: `Selected ${ids.length} ${ids.length === 1 ? "entity has" : "entities have"} been deleted`,
    });
  }

  return (
    <div className="flex min-h-screen flex-1 flex-col bg-grey-25">
      <div className="flex flex-col px-4 pt-4 pb-2 sm:px-8">
        <MonitoringHeader
          total={kpi.total}
          matched={kpi.matched}
          matchRate={kpi.matchRate}
          kpiLoading={kpiLoading}
          status={processingStatus}
          showUploadBatch
          showDownloadReport
          canManageEntries={canManageEntries}
          onNewMonitoring={onNewMonitoring}
          onUploadBatch={onUploadBatch}
          onDownloadReport={handleDownloadReport}
        />
      </div>

      <div className="@container sticky top-0 z-20 bg-grey-25 px-4 py-2 sm:px-8">
        <Toolbar
          query={query}
          onQueryChange={onQueryChange}
          searching={searching}
          disabled={!searching && count === 0}
          filter={filter}
          onFilterChange={onFilterChange}
          selectMode={selectMode}
          onToggleSelectMode={() =>
            setSelectMode((v) => {
              if (v) setSelectedIds(new Set());
              return !v;
            })
          }
          selectedCount={visibleSelectedIds.length}
          onDeleteSelected={() => {
            setDeleteError(undefined);
            setConfirmDeleteOpen(true);
          }}
          canManageEntries={canManageEntries}
          extra={
            <TableColumnsMenu
              columns={ENTRIES_COLUMNS_META}
              hidden={hidden}
              onToggle={(id) => onToggleHidden(id as ColumnId)}
              onReset={onResetColumns}
              disabled={!searching && count === 0}
            />
          }
        />
      </div>

      <div className="flex-1 px-4 pb-2 sm:px-8">
        {sorted.length === 0 ? (
          <div className="rounded-[4px] border border-grey-200 bg-white">
            <FilteredEmpty onReset={resetFilters} />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[4px] border border-grey-200 bg-white">
            <div className="min-w-[1084px]">
              <EntriesTableHeader
                order={order}
                hidden={hidden}
                onReorder={onReorder}
                selectMode={selectMode}
                allSelected={allSelected}
                someSelected={someSelected}
                onToggleAll={toggleAll}
                sort={sort}
                onSort={handleSort}
                widths={widths}
                onResizeColumn={onResizeColumn}
              />
              <EntriesTable
                entries={sorted}
                order={order}
                hidden={hidden}
                onSelect={(id) => navigate(`/monitoring-portal/${id}`)}
                selectMode={selectMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                widths={widths}
              />
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 z-20 bg-grey-25 px-4 pb-4 pt-2 sm:px-8">
        <PaginationFooter
          total={count}
          totalLabel="entities"
          individuals={entries.filter((e) => e.type === "Individual").length}
          organizations={entries.filter((e) => e.type === "Organization").length}
          others={
            entries.filter(
              (e) => e.type === "Vessel" || e.type === "Aircraft" || e.type === "Country",
            ).length
          }
          page={page}
          pageCount={pageCount}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
        destructive
        busy={deleting}
        busyLabel="Deleting…"
        error={deleteError}
        title={
          visibleSelectedIds.length === 1
            ? "Delete this monitored name?"
            : `Delete ${visibleSelectedIds.length} monitored names?`
        }
        description={
          visibleSelectedIds.length === 1
            ? "This stops monitoring the name and removes it from the list. It can't be undone."
            : `This stops monitoring these ${visibleSelectedIds.length} names and removes them from the list. It can't be undone.`
        }
        confirmLabel={visibleSelectedIds.length === 1 ? "Delete name" : "Delete names"}
      />
    </div>
  );
}
function FilteredEmpty({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-4 py-[120px]">
      <div className="flex flex-col items-center gap-1.5 text-center">
        <Icon name="search_off" size={40} weight={300} className="text-grey-500" />
        <h2 className="text-h4 font-semibold text-black">No entries match your filters</h2>
        <p className="w-[260px] text-small text-grey-900">
          Try a different search or filter to find what you&rsquo;re looking for.
        </p>
      </div>
      <Button hierarchy="secondary" size="sm" onClick={onReset}>
        Clear filters
      </Button>
    </div>
  );
}
